import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { ShieldCheck, ShieldOff } from "lucide-react";

export default function TwoFactorPanel() {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const [setup, setSetup] = useState(null); // {secret, otpauth_uri}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code });
      toast.success("2FA enabled");
      setSetup(null); setCode(""); await refreshUser();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA disabled");
      setCode(""); await refreshUser();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-panel border border-line rounded-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {user?.totp_enabled ? <ShieldCheck className="w-5 h-5 text-buy" /> : <ShieldOff className="w-5 h-5 text-dim" />}
          <h2 className="font-semibold text-sm uppercase tracking-widest">{t("two_fa")}</h2>
        </div>
        {user?.totp_enabled && <span data-testid="2fa-status" className="text-[10px] uppercase tracking-widest text-buy">{t("two_fa_enabled")}</span>}
      </div>
      {!user?.totp_enabled && !setup && (
        <button data-testid="2fa-setup-start" disabled={busy} onClick={start}
          className="bg-buy text-black font-bold px-4 py-2 rounded-sm hover:bg-emerald-400 uppercase text-xs tracking-widest disabled:opacity-50">
          {t("two_fa_enable")}
        </button>
      )}
      {!user?.totp_enabled && setup && (
        <div className="space-y-4">
          <p className="text-sm text-muted">{t("two_fa_setup_hint")}</p>
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="bg-white p-3 rounded-sm" data-testid="2fa-qrcode">
              <QRCodeSVG value={setup.otpauth_uri} size={160} bgColor="#fff" fgColor="#000" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-dim">Manual key</div>
              <div className="font-mono text-xs bg-app border border-line px-2 py-1.5 rounded-sm break-all" data-testid="2fa-secret">{setup.secret}</div>
              <input data-testid="2fa-verify-code" inputMode="numeric" maxLength={6} placeholder="123 456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g,""))}
                className="w-full bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono text-center tracking-widest text-lg" />
              <button data-testid="2fa-verify-submit" disabled={busy || code.length !== 6} onClick={verify}
                className="w-full bg-buy text-black font-bold py-2 rounded-sm hover:bg-emerald-400 uppercase text-xs tracking-widest disabled:opacity-50">
                {t("submit")}
              </button>
            </div>
          </div>
        </div>
      )}
      {user?.totp_enabled && (
        <div className="space-y-2">
          <input data-testid="2fa-disable-code" inputMode="numeric" maxLength={6} placeholder="Enter 6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g,""))}
            className="w-full bg-app border border-line px-3 py-2 rounded-sm outline-none font-mono text-center tracking-widest text-lg" />
          <button data-testid="2fa-disable-submit" disabled={busy || code.length !== 6} onClick={disable}
            className="w-full bg-sell text-white font-bold py-2 rounded-sm hover:bg-red-500 uppercase text-xs tracking-widest disabled:opacity-50">
            {t("two_fa_disable")}
          </button>
        </div>
      )}
    </div>
  );
}
